import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';
import './TargetImportModal.css';

/**
 * Monthly targets from one spreadsheet: download the template, have finance
 * fill it, upload, check the preview, submit.
 *
 * The server owns every rule (matching, scope, rollup, validation) — this
 * modal only shows what /target-import/preview answered. Submit sends the same
 * file again rather than the previewed rows, so the server re-checks exactly
 * what it writes. See backend salesTargetImportController.js.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const LEVEL_LABEL = { root: 'Group', company: 'Company', branch: 'Branch', salesman: 'Salesman' };
const STATUS_LABEL = { new: 'New', changed: 'Changed', unchanged: 'No change', blank: 'No target' };

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));

// An error on a blob request arrives as a Blob; read the JSON message out of it.
const blobErrorMessage = async (err, fallback) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
        try { return JSON.parse(await data.text()).message || fallback; } catch { return fallback; }
    }
    return data?.message || fallback;
};

const TargetImportModal = ({ show, onClose, onSuccess, defaultYear, defaultMonth }) => {
    const thisYear = new Date().getFullYear();
    const years = [thisYear - 1, thisYear, thisYear + 1];

    const [step, setStep] = useState('setup'); // setup | preview | done
    const [year, setYear] = useState(defaultYear || thisYear);
    const [month, setMonth] = useState(defaultMonth || new Date().getMonth() + 1);
    const [file, setFile] = useState(null);
    const [rollup, setRollup] = useState(true);
    const [dragActive, setDragActive] = useState(false);
    const [busy, setBusy] = useState(null); // 'download' | 'preview' | 'submit'
    const [error, setError] = useState(null);
    const [plan, setPlan] = useState(null);
    const [onlyChanges, setOnlyChanges] = useState(true);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (show) {
            setStep('setup');
            setYear(defaultYear || thisYear);
            setMonth(defaultMonth || new Date().getMonth() + 1);
            setFile(null);
            setRollup(true);
            setError(null);
            setPlan(null);
            setResult(null);
            setOnlyChanges(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const visibleRows = useMemo(() => {
        if (!plan) return [];
        return onlyChanges ? plan.rows.filter(r => r.status === 'new' || r.status === 'changed') : plan.rows;
    }, [plan, onlyChanges]);

    if (!show) return null;

    const handleClose = () => { if (!busy) onClose(); };

    const pickFile = (f) => {
        if (!f) return;
        if (!/\.xlsx$/i.test(f.name)) {
            setError('Please choose the .xlsx template downloaded from here');
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }
        setFile(f);
        setError(null);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        pickFile(e.dataTransfer.files?.[0]);
    };

    const downloadTemplate = async () => {
        setBusy('download');
        setError(null);
        try {
            const res = await axiosInstance.get('/api/sales-target/target-import/template', {
                params: { year, month },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Sales-Targets-${year}-${String(month).padStart(2, '0')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(await blobErrorMessage(err, 'Could not download the template'));
        } finally {
            setBusy(null);
        }
    };

    const buildForm = () => {
        const form = new FormData();
        form.append('file', file);
        form.append('rollup', rollup ? 'true' : 'false');
        form.append('year', year);
        form.append('month', month);
        return form;
    };

    const runPreview = async () => {
        if (!file) return;
        setBusy('preview');
        setError(null);
        try {
            const res = await axiosInstance.post('/api/sales-target/target-import/preview', buildForm());
            const data = res.data.data;
            setPlan(data);
            setOnlyChanges(data.summary.toWrite > 0);
            setStep('preview');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not read the file');
        } finally {
            setBusy(null);
        }
    };

    const submit = async () => {
        setBusy('submit');
        setError(null);
        try {
            const res = await axiosInstance.post('/api/sales-target/target-import', buildForm());
            setResult(res.data);
            setStep('done');
            onSuccess && onSuccess();
        } catch (err) {
            // A 400 carries a fresh plan (the data changed since the preview) — show it.
            if (err.response?.data?.data?.rows) setPlan(err.response.data.data);
            setError(err.response?.data?.message || 'Could not save the targets');
        } finally {
            setBusy(null);
        }
    };

    const planPeriodLabel = plan ? `${MONTHS[plan.period.month - 1]} ${plan.period.year}` : '';
    const periodDiffers = plan && (plan.period.year !== year || plan.period.month !== month);

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div
                className={`modal-content target-import-modal ${step === 'preview' ? 'is-wide' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <h2>Import Monthly Targets</h2>

                {error && <div className="error-message">{error}</div>}

                {step === 'setup' && (
                    <>
                        <div className="ti-step">
                            <div className="ti-step-num">1</div>
                            <div className="ti-step-body">
                                <h4>Download the template</h4>
                                <p>One row for the group, every company, branch and salesman you can set targets for — with this month's current target beside each.</p>
                                <div className="ti-period-row">
                                    <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} disabled={!!busy}>
                                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                                    </select>
                                    <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} disabled={!!busy}>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                    <button type="button" className="btn-secondary ti-download-btn" onClick={downloadTemplate} disabled={!!busy}>
                                        {busy === 'download' ? <span className="loading-spinner" /> : '⬇'} Download template
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="ti-step">
                            <div className="ti-step-num">2</div>
                            <div className="ti-step-body">
                                <h4>Fill the yellow “New target” column</h4>
                                <p>Leave a cell blank to keep its current target. Don’t change the Level, Code or hidden Ref columns.</p>
                            </div>
                        </div>

                        <div className="ti-step">
                            <div className="ti-step-num">3</div>
                            <div className="ti-step-body">
                                <h4>Upload it and check the preview</h4>
                                <div
                                    className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        id="target-import-file"
                                        accept=".xlsx"
                                        onChange={(e) => { pickFile(e.target.files[0]); e.target.value = ''; }}
                                        style={{ display: 'none' }}
                                        disabled={!!busy}
                                    />
                                    {file ? (
                                        <div className="file-selected">
                                            <div className="file-icon">📄</div>
                                            <div className="file-info">
                                                <div className="file-name">{file.name}</div>
                                                <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
                                            </div>
                                            <button type="button" className="remove-file-btn" onClick={() => setFile(null)} disabled={!!busy}>✕</button>
                                        </div>
                                    ) : (
                                        <div className="file-upload-prompt">
                                            <div className="upload-text"><strong>Drop the filled template here</strong> or</div>
                                            <label htmlFor="target-import-file" className="file-select-btn">Choose File</label>
                                        </div>
                                    )}
                                </div>
                                <label className="checkbox-label ti-rollup">
                                    <input type="checkbox" checked={rollup} onChange={e => setRollup(e.target.checked)} disabled={!!busy} />
                                    <span>Set blank Group / Company / Branch rows to the sum of the rows under them</span>
                                </label>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={handleClose} disabled={!!busy}>Cancel</button>
                            <button type="button" className="btn-primary" onClick={runPreview} disabled={!!busy || !file}>
                                {busy === 'preview' ? <><span className="loading-spinner" /> Reading…</> : 'Preview'}
                            </button>
                        </div>
                    </>
                )}

                {step === 'preview' && plan && (
                    <>
                        <p className="modal-subtitle">
                            Targets for <strong>{planPeriodLabel}</strong> from <strong>{file?.name}</strong>
                        </p>
                        {periodDiffers && (
                            <div className="ti-notice">
                                This file was downloaded for {planPeriodLabel}, not {MONTHS[month - 1]} {year}. It will be saved for {planPeriodLabel}.
                            </div>
                        )}

                        <div className="ti-summary">
                            <span className="ti-chip is-new">{plan.summary.new} new</span>
                            <span className="ti-chip is-changed">{plan.summary.changed} changed</span>
                            <span className="ti-chip">{plan.summary.unchanged} unchanged</span>
                            {plan.summary.errors > 0 && <span className="ti-chip is-error">{plan.summary.errors} errors</span>}
                            {plan.summary.warnings > 0 && <span className="ti-chip is-warning">{plan.summary.warnings} warnings</span>}
                            {plan.rows.some(r => r.level === 'root') && (
                                <span className="ti-total">Group total: <strong>{fmt(plan.summary.rootTotal)}</strong></span>
                            )}
                        </div>

                        {plan.errors.length > 0 && (
                            <div className="ti-issues is-error">
                                <h4>Fix these in the file and upload again — nothing can be saved until they are gone</h4>
                                <ul>{plan.errors.map((e, i) => <li key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</li>)}</ul>
                            </div>
                        )}
                        {plan.warnings.length > 0 && (
                            <details className="ti-issues is-warning" open={plan.warnings.length <= 5}>
                                <summary>Totals that don’t match the rows under them ({plan.warnings.length}) — allowed, but check them</summary>
                                <ul>{plan.warnings.map((w, i) => <li key={i}>{w.row ? `Row ${w.row}: ` : ''}{w.message}</li>)}</ul>
                            </details>
                        )}

                        <div className="ti-table-tools">
                            <label className="checkbox-label">
                                <input type="checkbox" checked={onlyChanges} onChange={e => setOnlyChanges(e.target.checked)} />
                                <span>Show only rows that will change</span>
                            </label>
                            <span className="ti-legend"><span className="ti-sum-badge">Σ</span> = sum of the rows under it</span>
                        </div>

                        <div className="ti-table-wrap">
                            <table className="ti-table">
                                <thead>
                                    <tr>
                                        <th>Level</th>
                                        <th>Name</th>
                                        <th className="ti-code">Code</th>
                                        <th className="num">Current</th>
                                        <th className="num">New</th>
                                        <th className="num">Change</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.length === 0 ? (
                                        <tr><td colSpan={7} className="ti-empty">Nothing will change.</td></tr>
                                    ) : visibleRows.map(r => {
                                        const delta = r.next !== null && r.current !== null ? r.next - r.current : null;
                                        return (
                                            <tr key={r.key} className={`lvl-${r.level}`}>
                                                <td><span className={`ti-level lvl-${r.level}`}>{LEVEL_LABEL[r.level]}</span></td>
                                                <td style={{ paddingLeft: `${10 + (onlyChanges ? 0 : r.depth * 14)}px` }}>
                                                    {r.name}
                                                    {onlyChanges && r.parentName && <span className="ti-parent"> · {r.parentName}</span>}
                                                </td>
                                                <td className="ti-code">{r.code}</td>
                                                <td className="num">{fmt(r.current)}</td>
                                                <td className="num ti-next">
                                                    {r.source === 'rollup' && <span className="ti-sum-badge" title="Sum of the rows under it">Σ</span>}
                                                    {fmt(r.next)}
                                                </td>
                                                <td className={`num ${delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''}`}>
                                                    {delta ? `${delta > 0 ? '+' : ''}${fmt(delta)}` : ''}
                                                </td>
                                                <td><span className={`ti-status is-${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={() => { setStep('setup'); setError(null); }} disabled={!!busy}>
                                ← Choose another file
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={submit}
                                disabled={!!busy || plan.errors.length > 0 || plan.summary.toWrite === 0}
                            >
                                {busy === 'submit'
                                    ? <><span className="loading-spinner" /> Saving…</>
                                    : `Submit ${plan.summary.toWrite} target${plan.summary.toWrite === 1 ? '' : 's'}`}
                            </button>
                        </div>
                    </>
                )}

                {step === 'done' && result && (
                    <>
                        <div className="ti-done">
                            <div className="ti-done-icon">✓</div>
                            <p>{result.message}</p>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-primary" onClick={handleClose}>Close</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TargetImportModal;
